// frontend/src/pages/VendorGroupDiscovery.jsx - UPDATED WITH ANALYTICS MODAL
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  MapPin, 
  Users, 
  Clock, 
  TrendingUp, 
  MessageSquare, 
  ArrowLeft,
  Target,
  Star,
  DollarSign,
  Package,
  Calendar,
  Eye,
  Send,
  CheckCircle,
  AlertCircle,
  Zap
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { generateDemoGroups } from '../utils/groupData';
import vendorDataService from '../services/VendorDataService';
import GroupAnalyticsModal from '../components/vendor/GroupAnalyticsModal';

const VendorGroupDiscovery = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotification();
  
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [vendorProducts, setVendorProducts] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    minMembers: 0,
    maxDistance: 50,
    urgency: 'all',
    sortBy: 'match_score'
  });

  // Modal state
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const categories = [
    'all', 'Groceries', 'Household', 'Electronics', 'Personal Care', 
    'Baby', 'Home & Garden', 'Health & Wellness', 'Arts & Crafts'
  ];

  const urgencyLevels = [
    { value: 'all', label: 'All Groups' },
    { value: 'urgent', label: 'Urgent (< 7 days)' },
    { value: 'moderate', label: 'Moderate (7-30 days)' },
    { value: 'relaxed', label: 'Relaxed (> 30 days)' }
  ];

  const sortOptions = [
    { value: 'match_score', label: 'Best Match' },
    { value: 'members', label: 'Most Members' },
    { value: 'deadline', label: 'Deadline (Urgent First)' },
    { value: 'progress', label: 'Progress (Nearly Complete)' },
    { value: 'potential_value', label: 'Potential Revenue' }
  ];

  useEffect(() => {
    loadGroupData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [groups, filters]);

  const loadGroupData = async () => {
    setLoading(true);
    try {
      // Get current vendor
      const currentVendor = localStorage.getItem('currentVendor');
      if (currentVendor) {
        const vendor = JSON.parse(currentVendor);
        
        // Load vendor products to help with matching
        const products = vendorDataService.getVendorProducts(vendor.id);
        setVendorProducts(products);
      }

      // Load all available groups
      const allGroups = generateDemoGroups();
      
      // Filter and enhance groups for vendor targeting
      const targetableGroups = allGroups
        .filter(group => !group.isCompleted && group.currentCount >= 5)
        .map(group => enhanceGroupForVendor(group))
        .sort((a, b) => b.matchScore - a.matchScore);

      setGroups(targetableGroups);
      
    } catch (error) {
      console.error('Error loading group data:', error);
      addNotification({
        type: 'error',
        title: 'Loading Error',
        message: 'Failed to load group data',
        showToast: true
      });
    } finally {
      setLoading(false);
    }
  };

  const enhanceGroupForVendor = (group) => {
    // Calculate match score based on vendor products
    let matchScore = calculateMatchScore(group);
    
    // Calculate urgency
    const now = new Date();
    const deadline = new Date(group.endDate);
    const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    
    let urgency = 'relaxed';
    if (daysLeft <= 7) urgency = 'urgent';
    else if (daysLeft <= 30) urgency = 'moderate';
    
    // Calculate potential revenue
    const potentialRevenue = estimatePotentialRevenue(group);
    
    // Calculate targeting score
    const targetingScore = calculateTargetingScore(group, daysLeft);
    
    return {
      ...group,
      matchScore,
      urgency,
      daysLeft,
      potentialRevenue,
      targetingScore,
      adminContact: group.creatorName || 'Group Admin',
      estimatedSavings: Math.round(group.savings * 1.2), // Vendor could improve savings
      memberGrowthRate: Math.random() * 20 + 5 // Mock growth rate
    };
  };

  const calculateMatchScore = (group) => {
    if (vendorProducts.length === 0) return 50; // Default score
    
    // Check if vendor has products in group's category
    const hasMatchingCategory = vendorProducts.some(product => 
      product.category === group.category
    );
    
    if (hasMatchingCategory) return 85 + Math.random() * 15;
    return 30 + Math.random() * 40;
  };

  const estimatePotentialRevenue = (group) => {
    // Estimate based on group size and typical order values
    const avgOrderValue = group.currentPrice || 50;
    const memberCount = group.currentCount;
    return Math.round(avgOrderValue * memberCount * 0.7); // 70% participation estimate
  };

  const calculateTargetingScore = (group, daysLeft) => {
    let score = 0;
    
    // Group size bonus
    score += Math.min(group.currentCount * 2, 40);
    
    // Urgency bonus
    if (daysLeft <= 7) score += 30;
    else if (daysLeft <= 14) score += 20;
    else if (daysLeft <= 30) score += 10;
    
    // Progress bonus
    if (group.progress > 80) score += 20;
    else if (group.progress > 60) score += 15;
    else if (group.progress > 40) score += 10;
    
    // Trust bonus
    if (group.creatorTrust && group.creatorTrust > 3.5) score += 10;
    
    return Math.min(score, 100);
  };

  const applyFilters = () => {
    let filtered = [...groups];
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(group => 
        group.title.toLowerCase().includes(searchLower) ||
        group.category.toLowerCase().includes(searchLower) ||
        group.location.toLowerCase().includes(searchLower)
      );
    }
    
    // Category filter
    if (filters.category !== 'all') {
      filtered = filtered.filter(group => group.category === filters.category);
    }
    
    // Minimum members filter
    if (filters.minMembers > 0) {
      filtered = filtered.filter(group => group.currentCount >= filters.minMembers);
    }
    
    // Urgency filter
    if (filters.urgency !== 'all') {
      filtered = filtered.filter(group => group.urgency === filters.urgency);
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'members':
          return b.currentCount - a.currentCount;
        case 'deadline':
          return a.daysLeft - b.daysLeft;
        case 'progress':
          return b.progress - a.progress;
        case 'potential_value':
          return b.potentialRevenue - a.potentialRevenue;
        case 'match_score':
        default:
          return b.matchScore - a.matchScore;
      }
    });
    
    setFilteredGroups(filtered);
  };

  const handleContactGroup = (group) => {
    // Navigate to bulk order creation with pre-selected group
    navigate(`/vendor/bulk-order/${group.id}`);
  };

  // UPDATED: Enhanced group details view with analytics modal
  const handleViewGroupDetails = (group) => {
    setSelectedGroup(group);
    setShowAnalyticsModal(true);
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'moderate': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'relaxed': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getMatchScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getTargetingAdvice = (group) => {
    if (group.urgency === 'urgent' && group.progress > 70) {
      return "🔥 Act fast! This group is close to completing and needs immediate attention.";
    }
    if (group.matchScore > 80) {
      return "🎯 Perfect match! Your products align well with this group's needs.";
    }
    if (group.potentialRevenue > 2000) {
      return "💰 High value opportunity with significant revenue potential.";
    }
    if (group.currentCount > 30) {
      return "👥 Large group with strong member engagement.";
    }
    return "📈 Good opportunity for building vendor relationships.";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Finding groups for you...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/vendor/dashboard')}
                className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
              >
                <ArrowLeft size={20} className="mr-2" />
                Back to Dashboard
              </button>
              <div>
                <h1 className="text-2xl font-bold">Group Discovery</h1>
                <p className="text-sm text-gray-600">Find community groups ready for your products</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {filteredGroups.length} groups found
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search groups by name, category, or location..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>

            {/* Urgency */}
            <div>
              <select
                value={filters.urgency}
                onChange={(e) => setFilters(prev => ({ ...prev, urgency: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {urgencyLevels.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Min Members */}
            <div>
              <input
                type="number"
                placeholder="Min members"
                value={filters.minMembers}
                onChange={(e) => setFilters(prev => ({ ...prev, minMembers: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Sort */}
            <div>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Groups Grid */}
        {filteredGroups.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGroups.map(group => (
              <div key={group.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow border">
                <div className="p-6">
                  {/* Group Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 mb-1">{group.title}</h3>
                      <p className="text-sm text-gray-600">{group.category}</p>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getMatchScoreColor(group.matchScore)}`}>
                        {group.matchScore > 80 ? 'Perfect Match' :
                         group.matchScore > 60 ? 'Good Match' : 'Potential'}
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getUrgencyColor(group.urgency)}`}>
                        {group.urgency === 'urgent' ? (
                          <div className="flex items-center">
                            <Zap size={10} className="mr-1" />
                            Urgent
                          </div>
                        ) : group.urgency.charAt(0).toUpperCase() + group.urgency.slice(1)}
                      </div>
                    </div>
                  </div>

                  {/* Targeting Advice */}
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">{getTargetingAdvice(group)}</p>
                  </div>

                  {/* Group Stats */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Members:</span>
                      <span className="font-medium flex items-center">
                        <Users size={14} className="mr-1" />
                        {group.currentCount}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Progress:</span>
                      <span className="font-medium">{group.progress}% complete</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Potential Revenue:</span>
                      <span className="font-medium text-green-600">${group.potentialRevenue}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Time Left:</span>
                      <span className={`font-medium ${group.urgency === 'urgent' ? 'text-red-600' : 'text-gray-900'}`}>
                        {group.daysLeft > 0 ? `${group.daysLeft} days` : 'Ending soon'}
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>{group.currentUnits} units</span>
                      <span>{group.nextBreakCount} needed</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          group.progress > 80 ? 'bg-green-500' : 
                          group.progress > 60 ? 'bg-yellow-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${Math.min(group.progress, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Location & Admin */}
                  <div className="space-y-2 mb-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <MapPin size={14} className="mr-1" />
                      <span>{group.location}</span>
                    </div>
                    <div className="flex items-center">
                      <Users size={14} className="mr-1" />
                      <span>Admin: {group.adminContact}</span>
                      {group.creatorTrust && (
                        <span className="ml-2 flex items-center">
                          <Star size={12} className="text-yellow-500 fill-current mr-1" />
                          <span className="text-xs">{group.creatorTrust}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleContactGroup(group)}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center justify-center"
                    >
                      <Send size={16} className="mr-2" />
                      Create Offer
                    </button>
                    <button
                      onClick={() => handleViewGroupDetails(group)}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title="View detailed analytics"
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Target size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Groups Found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search terms or filters to find targetable groups.</p>
            <div className="space-y-2">
              <button
                onClick={() => setFilters({
                  search: '',
                  category: 'all',
                  minMembers: 0,
                  maxDistance: 50,
                  urgency: 'all',
                  sortBy: 'match_score'
                })}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-3">How Group Discovery Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
            <div className="flex items-start">
              <CheckCircle size={16} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Perfect Match Groups</p>
                <p>Groups that match your product categories and have high engagement.</p>
              </div>
            </div>
            <div className="flex items-start">
              <Clock size={16} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Urgent Opportunities</p>
                <p>Groups close to their deadline that need immediate vendor attention.</p>
              </div>
            </div>
            <div className="flex items-start">
              <DollarSign size={16} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Revenue Potential</p>
                <p>Estimated revenue based on group size and typical order values.</p>
              </div>
            </div>
            <div className="flex items-start">
              <Eye size={16} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Detailed Analytics</p>
                <p>Click the eye icon for comprehensive group intelligence and insights.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Modal */}
      <GroupAnalyticsModal
        group={selectedGroup}
        isOpen={showAnalyticsModal}
        onClose={() => {
          setShowAnalyticsModal(false);
          setSelectedGroup(null);
        }}
      />
    </div>
  );
};

export default VendorGroupDiscovery;